import { useMemo } from 'react';

import {
  Text,
  FormControl,
  FormLabel,
  Select,
  RadioGroup,
  Radio,
  HStack,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Input,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
} from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';
import { useAppSelector, useAppDispatch } from 'hooks';
import {
  setPaymentDateRequested,
  setPaymentType,
  setPaymentMethod,
  setDownPayment,
  setDownPaymentAmount,
  setDownPaymentType,
  setAgencyBillingFinancingType,
  setAgencyBillingSignedContractAttached,
  setAgencyBillingProvinceCode,
  setAgencyBillingTotalPremium,
  setPremiumConfirmed,
  setAgencyBillingCompanyFee,
  setAgencyBillingBrokerFee,
  setAgencyBillingTax,
  useLegacyAutoFormLocked,
  PaymentType,
  PaymentMethod,
  DownPaymentType,
  FinancingType,
} from 'reducers/legacyAutoFormReducer';
import { apiGetApplicationPayments } from 'api';
import PaymentLink from 'views/shared/MethodOfPayment/components/PaymentLink';
import { PROVINCES } from 'variables/options';

const PAYMENT_DATE_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

const PAYMENT_TYPE_OPTIONS: { value: PaymentType; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: '3_pay', label: '3 Pay' },
  { value: 'annual', label: 'Annual' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'eft', label: 'EFT' },
  { value: 'direct_bill', label: 'Direct Bill Invoice' },
  { value: 'agency_bill', label: 'Agency Bill Invoice' },
  { value: 'other', label: 'Other' },
];

const DOWN_PAYMENT_TYPE_OPTIONS: { value: DownPaymentType; label: string }[] = [
  { value: 'e_transfer', label: 'E-transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const FINANCING_TYPE_OPTIONS: { value: FinancingType; label: string }[] = [
  { value: 'paid_full', label: 'Paid Full' },
  { value: 'premium_financing', label: 'Premium Financing' },
];

// Filter out Ontario from provinces for agency billing
const AGENCY_BILLING_PROVINCES = PROVINCES.filter((p) => p.value !== 'ON');

interface BillingSectionProps {
  policyUuid: string;
  canEditPremium?: boolean;
}

const BillingSection = ({ policyUuid, canEditPremium = false }: BillingSectionProps) => {
  const dispatch = useAppDispatch();
  const isLocked = useLegacyAutoFormLocked();

  const billing = useAppSelector((state) => state.legacyAutoForm.data.billing);
  const premiumConfirmed = useAppSelector(
    (state) => state.legacyAutoForm.premiumConfirmed,
  );
  const clientEmail = useAppSelector(
    (state) => state.legacyAutoForm.data.client_information.client_email,
  );
  const insuranceCompany = useAppSelector(
    (state) => state.legacyAutoForm.data.binder_information.insurance_company,
  );
  const hasUnsavedChanges = useAppSelector(
    (state) => state.legacyAutoForm.hasUnsavedChanges,
  );
  const isDataSaved = !hasUnsavedChanges;

  const totalInvoicedToCustomer = useMemo(() => {
    const premium = parseFloat(billing.agency_billing.total_premium) || 0;
    const companyFee = parseFloat(billing.agency_billing.company_fee) || 0;
    const brokerFee = parseFloat(billing.agency_billing.broker_fee) || 0;
    const tax = parseFloat(billing.agency_billing.tax) || 0;
    return (premium + companyFee + brokerFee + tax).toFixed(2);
  }, [
    billing.agency_billing.total_premium,
    billing.agency_billing.company_fee,
    billing.agency_billing.broker_fee,
    billing.agency_billing.tax,
  ]);

  const { data: paymentsData } = useQuery({
    queryKey: ['application-payments', policyUuid],
    queryFn: () => apiGetApplicationPayments(policyUuid),
    refetchInterval: 5000,
    enabled: !!policyUuid && !isLocked,
  });

  return (
    <Card mb="20px" id="sectionBilling">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Billing
      </Text>

      <VStack spacing={4} align="stretch">
        {/* Row 1: Payment Date, Payment Type, Payment Method */}
        <HStack spacing={4} align="flex-start">
          <FormControl flex={1} isRequired>
            <FormLabel>Payment Date Requested</FormLabel>
            <Select
              variant="main"
              value={billing.payment_date_requested}
              onChange={(e) =>
                dispatch(setPaymentDateRequested(e.target.value))
              }
              placeholder="Select date"
              isDisabled={isLocked}
            >
              {PAYMENT_DATE_OPTIONS.map((day) => (
                <option key={day} value={day.toString()}>
                  {day}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel>Payment Type</FormLabel>
            <Select
              variant="main"
              value={billing.payment_type}
              onChange={(e) =>
                dispatch(setPaymentType(e.target.value as PaymentType))
              }
              placeholder="Select type"
              isDisabled={isLocked}
            >
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel>Payment Method</FormLabel>
            <Select
              variant="main"
              value={billing.payment_method}
              onChange={(e) =>
                dispatch(setPaymentMethod(e.target.value as PaymentMethod))
              }
              placeholder="Select method"
              isDisabled={isLocked}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {billing.payment_method === 'credit_card' && !isLocked && (
              <PaymentLink
                paymentMethod={billing.payment_method}
                section="payment_method"
                accountHolderEmail={clientEmail}
                isDataSaved={isDataSaved}
                payments={paymentsData}
              />
            )}
            {billing.payment_method === 'eft' && !isLocked && (
              <>
                {insuranceCompany ? (
                  <PaymentLink
                    paymentMethod={billing.payment_method}
                    section="payment_method"
                    accountHolderEmail={clientEmail}
                    isDataSaved={isDataSaved}
                    payments={paymentsData}
                  />
                ) : (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Set Insurance Company to enable EFT payment options
                  </Text>
                )}
              </>
            )}
          </FormControl>
        </HStack>

        {/* Row 2: Down Payment */}
        <FormControl>
          <FormLabel>Down Payment</FormLabel>
          <RadioGroup
            value={billing.down_payment ? 'yes' : 'no'}
            onChange={(value) => dispatch(setDownPayment(value === 'yes'))}
            isDisabled={isLocked}
          >
            <HStack spacing={4}>
              <Radio value="yes" isDisabled={isLocked}>
                Yes
              </Radio>
              <Radio value="no" isDisabled={isLocked}>
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        {/* Row 3: Down Payment Details (conditional) */}
        {billing.down_payment && (
          <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200">
            <HStack spacing={4} align="flex-start">
              <FormControl flex={1} isRequired>
                <FormLabel>Down Payment Amount</FormLabel>
                <NumberInput
                  value={billing.down_payment_amount}
                  onChange={(valueString) => {
                    if (valueString === '') {
                      dispatch(setDownPaymentAmount(0));
                      return;
                    }
                    const value = parseFloat(valueString);
                    if (!isNaN(value)) {
                      dispatch(setDownPaymentAmount(value));
                    }
                  }}
                  min={0}
                  step={0.01}
                  precision={2}
                  isDisabled={isLocked}
                >
                  <NumberInputField placeholder="0.00" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl flex={1} isRequired>
                <FormLabel>Down Payment Type</FormLabel>
                <Select
                  variant="main"
                  value={billing.down_payment_type}
                  onChange={(e) =>
                    dispatch(
                      setDownPaymentType(e.target.value as DownPaymentType),
                    )
                  }
                  placeholder="Select type"
                  isDisabled={isLocked}
                >
                  {DOWN_PAYMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>
          </Box>
        )}

        {/* Agency Billing Fields (conditional) */}
        {billing.payment_method === 'agency_bill' && (
          <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200">
            <Text fontWeight="semibold" fontSize="md" mb={4}>
              Agency Billing Details
            </Text>
            <VStack spacing={4} align="stretch">
              {/* Financing Type */}
              <FormControl isRequired>
                <FormLabel>Financing</FormLabel>
                <RadioGroup
                  value={billing.agency_billing.financing_type}
                  onChange={(value) =>
                    dispatch(
                      setAgencyBillingFinancingType(value as FinancingType),
                    )
                  }
                  isDisabled={isLocked}
                >
                  <HStack spacing={4}>
                    {FINANCING_TYPE_OPTIONS.map((option) => (
                      <Radio
                        key={option.value}
                        value={option.value}
                        isDisabled={isLocked}
                      >
                        {option.label}
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </FormControl>

              {/* Signed Contract Attached (only for Premium Financing) */}
              {billing.agency_billing.financing_type ===
                'premium_financing' && (
                <FormControl isRequired>
                  <FormLabel>Signed Contract Attached</FormLabel>
                  <RadioGroup
                    value={
                      billing.agency_billing.is_signed_contract_attached
                        ? 'yes'
                        : 'no'
                    }
                    onChange={(value) =>
                      dispatch(
                        setAgencyBillingSignedContractAttached(value === 'yes'),
                      )
                    }
                    isDisabled={isLocked}
                  >
                    <HStack spacing={4}>
                      <Radio value="yes" isDisabled={isLocked}>
                        Yes
                      </Radio>
                      <Radio value="no" isDisabled={isLocked}>
                        No
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </FormControl>
              )}

              {/* Province */}
              <FormControl isRequired>
                <FormLabel>Province</FormLabel>
                <SelectSearch
                  value={billing.agency_billing.province_code}
                  onChange={(value) =>
                    dispatch(setAgencyBillingProvinceCode(value as string))
                  }
                  placeholder="Select province"
                  isDisabled={isLocked}
                  options={AGENCY_BILLING_PROVINCES}
                />
              </FormControl>

              {/* Total Premium, Company Fee, Broker Fee, Tax */}
              <SimpleGrid columns={4} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Total Premium</FormLabel>
                  <NumberInput
                    value={billing.agency_billing.total_premium}
                    onChange={(valueString) => {
                      dispatch(setAgencyBillingTotalPremium(valueString));
                    }}
                    min={0}
                    step={0.01}
                    precision={2}
                    isDisabled={isLocked && !canEditPremium}
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                  {parseFloat(billing.agency_billing.total_premium || '0') > 10000 && (
                    <Alert status="warning" mt={2} flexDirection="column" alignItems="flex-start">
                      <HStack mb={2}>
                        <AlertIcon />
                        <AlertDescription>
                          Premium amount exceeds $10,000. Please verify this amount is correct.
                        </AlertDescription>
                      </HStack>
                      <Checkbox
                        ml={8}
                        isChecked={premiumConfirmed}
                        onChange={(e) => dispatch(setPremiumConfirmed(e.target.checked))}
                        colorScheme="orange"
                        isRequired
                        isDisabled={isLocked}
                      >
                        <Text fontWeight="bold">
                          I confirm the premium amount is correct
                        </Text>
                      </Checkbox>
                    </Alert>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Company Fee</FormLabel>
                  <NumberInput
                    value={billing.agency_billing.company_fee}
                    onChange={(valueString) => {
                      dispatch(setAgencyBillingCompanyFee(valueString));
                    }}
                    min={0}
                    step={0.01}
                    precision={2}
                    isDisabled={isLocked}
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Broker Fee</FormLabel>
                  <NumberInput
                    value={billing.agency_billing.broker_fee}
                    onChange={(valueString) => {
                      dispatch(setAgencyBillingBrokerFee(valueString));
                    }}
                    min={0}
                    step={0.01}
                    precision={2}
                    isDisabled={isLocked}
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Tax</FormLabel>
                  <NumberInput
                    value={billing.agency_billing.tax}
                    onChange={(valueString) => {
                      dispatch(setAgencyBillingTax(valueString));
                    }}
                    min={0}
                    step={0.01}
                    precision={2}
                    isDisabled={isLocked}
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              {/* Total Invoiced To Customer (calculated) */}
              <FormControl>
                <FormLabel>Total Invoiced To Customer</FormLabel>
                <Input
                  variant="main"
                  value={totalInvoicedToCustomer}
                  isReadOnly
                  bg="gray.100"
                />
              </FormControl>
            </VStack>
          </Box>
        )}
      </VStack>
    </Card>
  );
};

export default BillingSection;
